import { get } from 'lodash';
import { SEARCH_TYPE } from '../../components/search/SearchTypeEnum';
import {
  isEstablishmentBlockedForExams,
  isEstablishmentBlockedForFormulas,
  isEstablishmentBlockedForMedicalCertificates,
  isEstablishmentBlockedForMedicalReferrals,
  isEstablishmentBlockedForMedicalReports,
  isEstablishmentBlockedForMedicines,
} from '../../helpers/blockedEstablishment';
import { DoctorPermissions, Origin } from '../../interfaces/users';
import { PrescriberConfiguration } from '../../shared/prescriber';

export interface ModuleAvailabilityParams {
  origin?: Origin | null;
  doctorPermissions?: DoctorPermissions | null;
  allPermissionsFalse: boolean;
  hasCRMorCRO: boolean;
  prescriberConfiguration?: PrescriberConfiguration | null;
}

/**
 * O bloqueio por estabelecimento é a fonte de verdade da API — ela recusa a inclusão
 * com HTTP 401 independentemente da permissão individual do prescritor. Por isso ele
 * é avaliado antes de `doctorPermissions` em todas as funções abaixo.
 */
export const canShowPrescription = ({
  origin,
  doctorPermissions,
  allPermissionsFalse,
  hasCRMorCRO,
}: ModuleAvailabilityParams): boolean => {
  if (isEstablishmentBlockedForMedicines(origin)) return false;

  if (!allPermissionsFalse && !hasCRMorCRO && typeof doctorPermissions?.Prescription === 'boolean') {
    return doctorPermissions.Prescription;
  }

  return true;
};

export const canShowFormula = ({ origin, prescriberConfiguration }: ModuleAvailabilityParams): boolean => {
  if (isEstablishmentBlockedForFormulas(origin)) return false;

  return Boolean(get(prescriberConfiguration, 'Features.compoundedFormula', false));
};

export const canAddExam = ({
  origin,
  doctorPermissions,
  allPermissionsFalse,
  hasCRMorCRO,
  prescriberConfiguration,
}: ModuleAvailabilityParams): boolean => {
  if (isEstablishmentBlockedForExams(origin)) return false;

  if (!allPermissionsFalse && !hasCRMorCRO && typeof doctorPermissions?.Exam === 'boolean') {
    return doctorPermissions.Exam;
  }

  if (prescriberConfiguration) return !!prescriberConfiguration.HabilitarExame;
  return false;
};

export const canAddMedicalCertificate = ({
  origin,
  doctorPermissions,
  allPermissionsFalse,
  hasCRMorCRO,
  prescriberConfiguration,
}: ModuleAvailabilityParams): boolean => {
  if (isEstablishmentBlockedForMedicalCertificates(origin)) return false;

  if (!allPermissionsFalse && !hasCRMorCRO && typeof doctorPermissions?.MedicalStatement === 'boolean') {
    return doctorPermissions.MedicalStatement;
  }

  return prescriberConfiguration ? !!prescriberConfiguration.HabilitarAtestado : false;
};

export const canAddMedicalReferral = ({
  origin,
  doctorPermissions,
  allPermissionsFalse,
  hasCRMorCRO,
  prescriberConfiguration,
}: ModuleAvailabilityParams): boolean => {
  if (isEstablishmentBlockedForMedicalReferrals(origin)) return false;

  if (!prescriberConfiguration) return false;

  if (!allPermissionsFalse && !hasCRMorCRO && typeof doctorPermissions?.Referral === 'boolean') {
    return doctorPermissions.Referral;
  }

  return get(prescriberConfiguration, 'Features.enableReferral', false);
};

export const canAddMedicalReport = ({
  origin,
  doctorPermissions,
  allPermissionsFalse,
  hasCRMorCRO,
  prescriberConfiguration,
}: ModuleAvailabilityParams): boolean => {
  if (isEstablishmentBlockedForMedicalReports(origin)) return false;

  if (!prescriberConfiguration) return false;

  if (!allPermissionsFalse && !hasCRMorCRO && typeof doctorPermissions?.MedicalReport === 'boolean') {
    return doctorPermissions.MedicalReport;
  }

  return get(prescriberConfiguration, 'Features.enableMedicalReport', false);
};

export interface SearchableTabsVisibility {
  canShowPrescription: boolean;
  canShowFormula: boolean;
  canAddExam: boolean;
}

/**
 * Abas com barra de busca, na ordem do seletor. A receita deixa de ser a aba padrão
 * quando a unidade não está liberada para ela — o padrão passa a ser a primeira
 * aba de busca visível, ou nenhuma, se só sobrarem módulos sem busca.
 */
export const getVisibleSearchableTypes = ({
  canShowPrescription,
  canShowFormula,
  canAddExam,
}: SearchableTabsVisibility): SEARCH_TYPE[] =>
  [
    canShowPrescription && SEARCH_TYPE.MEDICINE,
    canShowFormula && SEARCH_TYPE.FORMULA,
    canAddExam && SEARCH_TYPE.EXAMS,
  ].filter((type): type is SEARCH_TYPE => Boolean(type));

export type NonSearchableTabAction = 'open' | 'reset' | 'wait';

/**
 * O que fazer quando a barra aponta para uma aba sem busca (atestado, encaminhamento, relatório),
 * inclusive quando ela chega pela AbaAtiva do backend, sem passar pelo seletor. O bloqueio da
 * unidade chega junto com a prescrição e volta para a aba padrão na hora. Fora dele, enquanto as
 * configurações do prescritor não carregam, a disponibilidade pode estar falsa só por enquanto
 * (`hasCRMorCRO` chega depois), então espera; com tudo carregado, módulo indisponível volta
 * para a aba padrão em vez de deixar a barra numa aba que não aparece.
 */
export const resolveNonSearchableTabAction = (
  canAdd: boolean,
  isAvailabilityResolved: boolean,
  isBlockedForEstablishment = false
): NonSearchableTabAction => {
  if (isBlockedForEstablishment) return 'reset';
  if (canAdd) return 'open';
  return isAvailabilityResolved ? 'reset' : 'wait';
};
